import { CommonModule } from "@angular/common";
import { Component, Input, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgbModal, NgbModalRef, NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { BarcodeFormat } from "@zxing/library";
import { ZXingScannerModule } from "@zxing/ngx-scanner";
import { PaginatorModule } from "primeng/paginator";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { TableModule } from "primeng/table";
import { finalize, firstValueFrom } from "rxjs";
import { AppLayoutComponent } from "../../../../../shared/components/app-layout/app-layout.component";
import { CameraService } from "../../../../services/camera.service";
import { EventsManagementService } from "../../../../services/events-management.service";
import { LocalStorageService } from "../../../../services/local-storage.service";
import { SnackBarService } from "../../../../services/snack-bar.service";
import { EventInfoResponseBody } from "../../../../services/types/events-management-response.type";

export interface CheckIn {
  id: number;
  user_name: string;
  status: string;
  user_last_name: string;
  user_cpf: string;
  user_email: string;
  event_title: string;
  event_start_date: string;
  event_end_date: string;
  ticket_title: string;
  ticket_price: string;
  scanned_at: string;
  scanned_by: string;
}

@Component({
  selector: "app-check-in-section",
  imports: [
    NgbModule,
    ZXingScannerModule,
    CommonModule,
    AppLayoutComponent,
    FormsModule,
    ProgressSpinnerModule,
    TableModule,
    PaginatorModule,
  ],
  templateUrl: "./check-in-section.component.html",
  styleUrl: "./check-in-section.component.scss",
})
export class CheckInSectionComponent implements OnInit {
  private modalRef: NgbModalRef | undefined;

  @Input() eventId?: number | null;

  first = 0;
  rows = 10;
  checkIns: CheckIn[] = [];
  eventInfo: EventInfoResponseBody = {
    id: "545",
    title: "Festa dos Corin",
  } as EventInfoResponseBody;

  newEventId: number | string = this.eventInfo.id;
  showUpdateEventForm: boolean = false;

  filteredCheckIns: CheckIn[] = [];
  paginatedCheckIns: CheckIn[] = [];

  currentPage = 1;
  itemsPerPage = 5;
  itemsPerPageOptions = [5, 10, 20, 50];
  searchTerm: string = "";
  scanResult!: any;
  loading = false;
  hasCameraPermission: boolean = false;
  videoConfig = {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    facingMode: "environment", // ou 'user', para câmera frontal
  };
  formats = [BarcodeFormat.QR_CODE];

  constructor(
    private readonly eventsManagementService: EventsManagementService,
    private readonly snackbarService: SnackBarService,
    private readonly modalService: NgbModal,
    private localStorageService: LocalStorageService,
    private cameraService: CameraService,
  ) {}

  async requestCameraAccess() {
    this.hasCameraPermission =
      await this.cameraService.checkCameraPermissions();
  }

  highlightFirstRow() {
    setTimeout(() => {
      const firstRow = document.querySelector("tbody tr");
      if (firstRow) {
        firstRow.classList.add("table-row-highlight");

        // Remover a classe após a animação para evitar reaplicação desnecessária
        setTimeout(() => {
          firstRow.classList.remove("table-row-highlight");
        }, 1500);
      }
    }, 0);
  }

  loadEventInfo(eventId: number) {
    this.eventsManagementService.getEventInfo(eventId).subscribe({
      next: (response) => {
        if (response.id) {
          this.eventInfo = { ...response };
        }
      },
      error: (error) => {
        console.error("Erro ao info do evento:", error);
      },
    });
  }

  updateEventInfo() {
    this.showUpdateEventForm = false;

    if (this.eventId) {
      this.loadEventInfo(this.eventId);
      this.loadEventScans();
    }
  }

  resetTable() {
    this.checkIns = [];
    this.filteredCheckIns = [...this.checkIns];
    this.updatePaginatedCheckIns();
  }

  loadEventScans() {
    this.loading = true;
    try {
      this.eventsManagementService
        .getEventScans(Number(this.eventId))
        .subscribe({
          next: (response) => {
            console.log("RESPONSE SCANS", response);
            if (response?.length > 0) {
              this.checkIns = response.sort((a, b) => b.id - a.id);
              this.filteredCheckIns = [...this.checkIns];
              this.updatePagination();
              this.highlightFirstRow();
            } else {
              this.resetTable();
            }
          },
          error: (error) => {
            console.error("Erro ao carregar o scans:", error);
            if (error?.error?.error) {
              this.snackbarService.openSnackBar(
                error?.error?.error,
                "Fechar",
                "error",
              );
            }
            this.resetTable();
          },
        });
    } catch (error) {
      console.log("Erro ao carregar scans", error);
      this.resetTable();
    } finally {
      this.loading = false;
      this.updatePaginatedCheckIns();
    }
  }

  toggleUpdateForm() {
    this.showUpdateEventForm = !this.showUpdateEventForm;
  }

  ngOnInit(): void {
    if (this.eventId) {
      this.updateEventInfo();
    }
  }

  downloadReport() {
    if (!this.eventId) return;
    this.loading = true;
    this.eventsManagementService
      .downloadTicketScansReport(Number(this.eventId))
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob as Blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "relatorio_leituras.pdf";
          a.click();
          URL.revokeObjectURL(url);
        },
      });
  }

  filterCheckIns() {
    if (this.searchTerm) {
      this.filteredCheckIns = this.checkIns.filter(
        (checkIn) =>
          String(checkIn.id)
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase()) ||
          String(checkIn.user_name + " " + checkIn.user_last_name)
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase()) ||
          (checkIn.user_email ?? "")
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase()) ||
          (checkIn.user_cpf ?? "").includes(this.searchTerm),
      );
    } else {
      this.filteredCheckIns = [...this.checkIns];
    }

    this.first = 0; // Resetar para a primeira página ao filtrar
    this.currentPage = 1;
    this.updatePaginatedCheckIns();
  }

  updatePaginatedCheckIns() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedCheckIns = this.filteredCheckIns.slice(startIndex, endIndex);
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    this.currentPage = Math.floor(event.first / event.rows) + 1;
    this.itemsPerPage = event.rows;
    this.updatePaginatedCheckIns();
  }

  updatePagination() {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedCheckIns = this.filteredCheckIns.slice(startIndex, endIndex);
  }

  nextPage() {
    if (this.hasNextPage()) {
      this.currentPage++;
      this.first = (this.currentPage - 1) * this.itemsPerPage;
      this.updatePaginatedCheckIns();
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.first = (this.currentPage - 1) * this.itemsPerPage;
      this.updatePaginatedCheckIns();
    }
  }

  goToPage(page: number) {
    this.currentPage = page;
    this.first = (this.currentPage - 1) * this.itemsPerPage;
    this.updatePaginatedCheckIns();
  }

  onItemsPerPageChange() {
    this.currentPage = 1;
    this.first = 0;
    this.rows = this.itemsPerPage;
    this.updatePaginatedCheckIns();
  }

  hasNextPage() {
    return this.currentPage * this.itemsPerPage < this.filteredCheckIns.length;
  }

  get totalPages() {
    return Math.ceil(this.filteredCheckIns.length / this.itemsPerPage);
  }

  get totalPagesArray() {
    return Array(Math.ceil(this.filteredCheckIns.length / this.itemsPerPage))
      .fill(0)
      .map((x, i) => i + 1);
  }

  getResultIcon(resultType: string) {
    switch (resultType) {
      case "success":
        return "pi pi-check-circle";
      case "error":
        return "pi pi-times-circle";
      default:
        return "";
    }
  }

  getResultColor(resultType: string) {
    switch (resultType) {
      case "success":
        return "#0ab75e";
      case "error":
        return "#b20000";
      default:
        return "";
    }
  }

  async onScanSuccess(result: string): Promise<void> {
    console.log("RESULT===========", result);
    this.loading = true;

    try {
      const response = await firstValueFrom(
        this.eventsManagementService.validateTicketScan(
          Number(this.eventInfo.id),
          JSON.parse(result),
        ),
      );

      console.log("RESPONSE SCAN TICKET", response);

      if (response.message === "Ticket is valid") {
        this.scanResult = {
          type: "success",
          message: "Ingresso válido! Bem-vindo ao evento.",
          data: response.data,
        };
        this.snackbarService.openSnackBar(
          "Ingresso válido! Bem-vindo ao evento.",
          "Fechar",
          "success",
        );
      } else {
        this.scanResult = {
          type: "error",
          message: response.message,
        };
        this.snackbarService.openSnackBar(response.message, "Fechar", "error");
      }
    } catch (error: any) {
      console.error("Erro ao escanear ticket:", error);

      if (error?.error?.message) {
        switch (error.error.message) {
          case "Sale not found":
            this.scanResult = {
              type: "error",
              message: "Não encontramos o ingresso.",
              complement:
                "O ingresso informado não foi encontrado. Verifique os dados e tente novamente.",
            };

            this.snackbarService.openSnackBar(
              "Não encontramos o ingresso. Verifique se você digitou corretamente ou tente novamente mais tarde.",
              "Fechar",
              "error",
              8000,
            );
            break;

          case "Ticket already used":
            this.scanResult = {
              type: "error",
              message: "O ingresso já foi utilizado.",
              complement:
                "Este ingresso já foi utilizado para acessar o evento. Caso tenha dúvidas, entre em contato com o suporte.",
            };

            this.snackbarService.openSnackBar(
              "Este ingresso já foi utilizado.",
              "Fechar",
              "error",
              8000,
            );
            break;

          case "Ticket is invalid":
            this.scanResult = {
              type: "error",
              message: "O ingresso informado é inválido.",
              complement:
                "O ingresso informado não é válido. Certifique-se de que está utilizando um ingresso correto.",
            };
            this.snackbarService.openSnackBar(
              "O ingresso informado é inválido. Por favor, verifique os detalhes e tente novamente.",
              "Fechar",
              "error",
              8000,
            );
            break;

          case "Ticket transferred":
            this.scanResult = {
              type: "error",
              message: "Este ingresso foi transferido para outra pessoa.",
              complement:
                "Este ingresso foi transferido para outra pessoa e não está mais vinculado ao seu nome. Entre em contato com o suporte, se necessário.",
            };
            this.snackbarService.openSnackBar(
              "Este ingresso foi transferido para outra pessoa. Verifique os detalhes da transferência ou entre em contato com o suporte se precisar de ajuda.",
              "Fechar",
              "error",
              8000,
            );
            break;

          default:
            this.scanResult = {
              type: "error",
              message:
                "Ocorreu um erro: " +
                error.error.message +
                ". Por favor, tente novamente ou entre em contato com o suporte.",
            };
            this.snackbarService.openSnackBar(
              "Ocorreu um erro: " +
                error.error.message +
                ". Por favor, tente novamente ou entre em contato com o suporte.",
              "Fechar",
              "error",
              8000,
            );
            break;
        }
      }
    } finally {
      // Finaliza o processo, ocultando o loader
      this.loading = false;
    }
  }

  openCameraModal(content: any) {
    this.requestCameraAccess();
    // @ts-ignore
    this.modalRef = this.modalService.open(content, { size: "lg" });
  }

  closeCameraModal() {
    if (this.modalRef) {
      this.modalRef.close();

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }

  maskCpf(cpf: string) {
    if (!cpf) return ""; // scans inválidos podem não ter CPF
    return cpf.slice(0, 3) + "********" + cpf.slice(8);
  }

  maskEmail(email: string): string {
    if (!email) return ""; // scans inválidos podem não ter e-mail
    const [localPart, domain] = email.split("@"); // Divide o e-mail em duas partes: antes e depois do "@"

    if (!localPart || !domain) {
      return "E-mail inválido";
    }

    // Exibe os dois primeiros caracteres do nome e o último, mascarando o resto
    const maskedLocalPart =
      localPart.length <= 2
        ? localPart[0] + "*".repeat(localPart.length - 2) // Caso o nome do e-mail seja muito curto
        : localPart[0] + "*".repeat(localPart.length - 4) + localPart.slice(-1);

    return `${maskedLocalPart}@${domain}`;
  }

  getScanStatus(status: string) {
    switch (status) {
      case "invalid":
        return "Inválido";
      case "valid":
        return "Válido";
      default:
        return status;
    }
  }
  getScanStatusColor(status: string) {
    switch (status) {
      case "valid":
        return "valid";
      case "invalid":
        return "invalid";
      default:
        return "default";
    }
  }
}
